import { useState } from 'react'
import { cn } from '@/lib/utils'
import { scentFamilies } from '@/data/scentFamilies'
import type { ScentFamily } from '@/types'
import { SlidersHorizontal, X } from 'lucide-react'

export type SortOption = 'featured' | 'price-asc' | 'price-desc' | 'newest'
export type TagOption = 'all' | 'new' | 'bestseller' | 'discovery-set' | 'refill'

interface FilterState {
  tag: TagOption
  families: ScentFamily[]
  concentration: string[]
  sort: SortOption
  priceMax: number | null
}

interface FilterBarProps {
  filters: FilterState
  onChange: (filters: FilterState) => void
}

const concentrations = ['EDT', 'EDP', 'Parfum', 'Extrait']

const tags: { id: TagOption; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'new', label: 'New' },
  { id: 'bestseller', label: 'Bestsellers' },
  { id: 'discovery-set', label: 'Discovery' },
  { id: 'refill', label: 'Refill' },
]

export function FilterBar({ filters, onChange }: FilterBarProps) {
  const [open, setOpen] = useState(false)

  const setTag = (tag: TagOption) => onChange({ ...filters, tag })

  const toggleFamily = (family: ScentFamily) => {
    const next = filters.families.includes(family)
      ? filters.families.filter((f) => f !== family)
      : [...filters.families, family]
    onChange({ ...filters, families: next })
  }

  const toggleConcentration = (c: string) => {
    const next = filters.concentration.includes(c)
      ? filters.concentration.filter((x) => x !== c)
      : [...filters.concentration, c]
    onChange({ ...filters, concentration: next })
  }

  const activeCount =
    (filters.tag !== 'all' ? 1 : 0) +
    filters.families.length +
    filters.concentration.length +
    (filters.priceMax ? 1 : 0)

  return (
    <div className="border-b border-line py-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          {tags.map((t) => {
            const active = filters.tag === t.id
            return (
              <button
                key={t.id}
                onClick={() => setTag(t.id)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs font-medium uppercase tracking-[0.12em] transition-colors',
                  active
                    ? 'border-wd-orange bg-wd-orange text-white'
                    : 'border-line/70 text-carbon hover:border-wd-orange hover:text-wd-orange'
                )}
              >
                {t.label}
              </button>
            )
          })}
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-2 text-sm text-carbon/60 hover:text-wd-orange"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filter {activeCount > 0 && <span className="text-carbon">({activeCount})</span>}
          </button>

          <select
            value={filters.sort}
            onChange={(e) => onChange({ ...filters, sort: e.target.value as SortOption })}
            className="border-b border-ink/10 bg-transparent pb-1 text-sm text-carbon focus:border-wd-orange focus:outline-none"
          >
            <option value="featured">Featured</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
            <option value="newest">Newest</option>
          </select>
        </div>
      </div>

      {open && (
        <div className="mt-6 grid gap-8 border-t border-line pt-6 md:grid-cols-3">
          <div>
            <h4 className="mb-3 font-condensed text-xs uppercase tracking-[0.2em] text-carbon/50">Scent family</h4>
            <div className="flex flex-wrap gap-2">
              {scentFamilies.map((f) => {
                const active = filters.families.includes(f.id)
                return (
                  <button
                    key={f.id}
                    onClick={() => toggleFamily(f.id)}
                    className={cn(
                      'chip-outline',
                      active && 'chip-active'
                    )}
                  >
                    {f.label}
                  </button>
                )
              })}
            </div>
          </div>

          <div>
            <h4 className="mb-3 font-condensed text-xs uppercase tracking-[0.2em] text-carbon/50">Concentration</h4>
            <div className="flex flex-wrap gap-2">
              {concentrations.map((c) => {
                const active = filters.concentration.includes(c)
                return (
                  <button
                    key={c}
                    onClick={() => toggleConcentration(c)}
                    className={cn(
                      'chip-outline',
                      active && 'chip-active'
                    )}
                  >
                    {c}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="flex items-start justify-between">
            <div className="space-y-3">
              <h4 className="font-condensed text-xs uppercase tracking-[0.2em] text-carbon/50">Max price</h4>
              <input
                type="range"
                min={20}
                max={200}
                step={10}
                value={filters.priceMax ?? 200}
                onChange={(e) => onChange({ ...filters, priceMax: parseInt(e.target.value, 10) })}
                className="w-full accent-wd-orange"
              />
              <p className="text-sm text-carbon">Up to ${filters.priceMax ?? 200}</p>
            </div>
            {activeCount > 0 && (
              <button
                onClick={() => onChange({ tag: 'all', families: [], concentration: [], sort: filters.sort, priceMax: null })}
                className="flex items-center gap-1 text-xs text-carbon/50 hover:text-wd-orange"
              >
                <X className="h-3 w-3" /> Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
